// v8.8g — Cold pairs badge.
//
// Visualises per-symbol cooldowns surfaced by /api/bot/status. The bot
// applies an escalating cooldown ladder (60s → 5m → 15m → 30m) when a
// symbol gets repeatedly rejected by the trend-alignment or BOS gates,
// so a chronically ranging pair drops out of rotation gracefully. This
// widget shows WHICH symbols are currently benched + their countdown.
//
// Polling: the parent CloudBotPanel already polls /api/bot/status
// every ~3s for the activity log, so we just read `status.cooldowns`
// from props. A local `tick` timer decrements the countdown each
// second for smooth animation between server refreshes.
import { useState, useEffect, useMemo } from "react";
import { Snowflake, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Friendly symbol formatter — matches the Trade-tab convention.
const pretty = (sym) => {
  if (sym.startsWith("cry")) return sym.replace("cry", "").replace("USD", "/USD");
  if (sym.startsWith("frx") && sym.length === 9) return `${sym.slice(3, 6)}/${sym.slice(6)}`;
  return sym;
};

// Map streak rung → ladder label (matches bot_worker COOLDOWN_LADDER).
const RUNG_LABEL = ["", "60s", "5m", "15m", "30m"];
const rungLabel = (streak) => RUNG_LABEL[Math.min(streak, RUNG_LABEL.length - 1)] || "30m";

// Color the rung — deeper red = longer cooldown.
const rungColor = (streak) => {
  if (streak >= 4) return "text-red-400 border-red-500/40 bg-red-500/10";
  if (streak >= 3) return "text-orange-400 border-orange-500/40 bg-orange-500/10";
  if (streak >= 2) return "text-amber-400 border-amber-500/40 bg-amber-500/10";
  return "text-vts-silver/70 border-vts-line bg-vts-bg/60";
};

const fmtSeconds = (s) => {
  if (s <= 0) return "0s";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
};

export function ColdPairsBadge({ cooldowns, accountId }) {
  // Local "ticking" countdown — server snapshot updates every ~3s, but we
  // want the per-card timer to tick every second between refreshes.
  const [tick, setTick] = useState(0);
  // Which symbol is being released (loading spinner placeholder).
  const [releasing, setReleasing] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const releaseSymbol = async (sym) => {
    if (!accountId) return;
    setReleasing(sym);
    try {
      const res = await api.post(
        `/bot/cooldown-clear/${encodeURIComponent(accountId)}/${encodeURIComponent(sym)}`,
      );
      if (res?.data?.ok) {
        toast.success(`${sym} released — back in scan rotation`);
      } else {
        toast.info(res?.data?.reason || `Couldn't release ${sym}`);
      }
    } catch {
      toast.error(`Failed to release ${sym}`);
    } finally {
      setReleasing(null);
    }
  };

  const entries = useMemo(() => {
    if (!cooldowns) return [];
    // Decrement client-side by `tick` seconds since last server snapshot
    // (best-effort smoothness; server payload re-syncs every poll).
    return Object.entries(cooldowns)
      .map(([sym, info]) => ({
        symbol: sym,
        remaining: Math.max(0, (info?.remaining_s || 0) - tick),
        streak: info?.streak || 0,
      }))
      .filter((e) => e.remaining > 0)
      .sort((a, b) => b.remaining - a.remaining);
  }, [cooldowns, tick]);

  if (entries.length === 0) return null;

  const top = entries.slice(0, 8);
  const overflow = entries.length - top.length;

  return (
    <div
      data-testid="cold-pairs-badge"
      className="mt-4 bg-vts-bg/60 border border-vts-line rounded-md px-4 py-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono flex items-center gap-1.5">
          <Snowflake className="w-3 h-3 text-cyan-400" />
          Cold pairs <span className="text-vts-silver/40">· {entries.length} benched</span>
        </div>
        <div className="text-[9px] font-mono text-vts-silver/40">
          ladder 60s · 5m · 15m · 30m
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {top.map((e) => (
          <div
            key={e.symbol}
            data-testid={`cold-pair-${e.symbol}`}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-mono ${rungColor(e.streak)}`}
            title={`${pretty(e.symbol)} on rung ${e.streak} (${rungLabel(e.streak)}) — ${e.remaining}s left · click ⛓ to release`}
          >
            <span className="text-vts-silver">{pretty(e.symbol)}</span>
            <span className="opacity-40">·</span>
            <span className="tabular-nums">{fmtSeconds(e.remaining)}</span>
            {e.streak >= 2 && (
              <span className="opacity-60 text-[8px] uppercase tracking-wider">
                rung {e.streak}
              </span>
            )}
            {/* v8.8h — per-chip override button. One click releases the
                symbol from cooldown + resets its streak. */}
            <button
              type="button"
              data-testid={`cold-pair-release-${e.symbol}`}
              onClick={() => releaseSymbol(e.symbol)}
              disabled={releasing === e.symbol}
              title="Release from cooldown"
              className="ml-0.5 p-0.5 rounded hover:bg-white/10 transition-colors disabled:opacity-40"
            >
              <Unlock className="w-2.5 h-2.5 opacity-70 hover:opacity-100" />
            </button>
          </div>
        ))}
        {overflow > 0 && (
          <div
            data-testid="cold-pairs-overflow"
            className="px-2 py-1 rounded-md border border-vts-line/40 text-[10px] font-mono text-vts-silver/50"
          >
            +{overflow} more
          </div>
        )}
      </div>
      <p className="text-[9px] font-mono text-vts-silver/40 mt-2 leading-snug">
        Symbols here are temporarily skipped after counter-trend or
        no-BOS rejections — the engine re-evaluates them when the
        countdown hits 0. Repeat rejections climb the ladder.
      </p>
    </div>
  );
}

export default ColdPairsBadge;
